'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface PricingTier {
  name: string;
  tier: string;
  price: number;
  description: string;
  popular?: boolean;
  features: string[];
  maxVehicles: number;
  maxDevices: number;
  maxSeats: number;
  cta: string;
  ctaHref: string;
}

export function PricingCard({
  tier,
  index,
  billingCycle = 'monthly',
  discount = 0,
}: {
  tier: PricingTier;
  index: number;
  billingCycle?: string;
  discount?: number;
}) {
  const adjustedPrice = tier.price * (1 - discount / 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className={`relative rounded-2xl border p-8 transition-all duration-300 hover:shadow-lg ${
        tier.popular
          ? 'border-emerald-200 bg-white shadow-lg shadow-emerald-500/5 ring-1 ring-emerald-500/20'
          : 'border-slate-200/60 bg-white shadow-sm hover:border-slate-300'
      }`}
    >
      {tier.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-4 py-1 text-xs font-semibold text-white shadow-md">
          Most Popular
        </span>
      )}

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-900">{tier.name}</h3>
        <p className="mt-1 text-sm text-slate-500">{tier.description}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-slate-900">${adjustedPrice.toFixed(0)}</span>
          <span className="text-sm text-slate-500">/mo</span>
        </div>
        {discount > 0 && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm text-slate-400 line-through">${tier.price}/mo</span>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Save {discount}%
            </span>
          </div>
        )}
        <p className="mt-2 text-xs text-slate-400">
          Up to {tier.maxVehicles} vehicles, {tier.maxDevices} devices, {tier.maxSeats} seats
        </p>
      </div>

      <Link href={tier.ctaHref}>
        <Button
          variant={tier.popular ? 'accent' : 'outline'}
          className={`w-full rounded-full ${tier.popular ? '' : 'border-slate-200'}`}
        >
          {tier.cta}
        </Button>
      </Link>

      <ul className="mt-8 space-y-3">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span className="text-sm text-slate-600">{feature}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export const pricingTiers: PricingTier[] = [
  {
    name: 'Basic',
    tier: 'basic',
    price: 49.99,
    description: 'Essential tracking for small fleets',
    features: [
      'Live GPS tracking',
      'Trip history & replay',
      'Basic reports (5 types)',
      'Email alerts',
      'Standard support',
    ],
    maxVehicles: 10,
    maxDevices: 20,
    maxSeats: 5,
    cta: 'Start Free Trial',
    ctaHref: '/signup?plan=basic',
  },
  {
    name: 'Safety',
    tier: 'safety',
    price: 149.99,
    description: 'Advanced safety & compliance tools',
    features: [
      'Everything in Basic',
      'Camera integration (2 channels)',
      'Driver safety scorecards',
      'Geofencing (radius & polygon)',
      '25+ report types',
      'SMS & email alerts',
      'Priority support',
    ],
    maxVehicles: 25,
    maxDevices: 50,
    maxSeats: 15,
    cta: 'Start Free Trial',
    ctaHref: '/signup?plan=safety',
  },
  {
    name: 'AI-Powered',
    tier: 'ai',
    price: 299.99,
    description: 'Full AI, camera & analytics suite',
    popular: true,
    features: [
      'Everything in Safety',
      'Quad-camera HLS streaming',
      'AI Copilot & anomaly detection',
      'ADAS / DMS automation',
      'UBI risk scoring',
      '75+ report types',
      'Webhook integrations',
      'Dedicated account manager',
    ],
    maxVehicles: 50,
    maxDevices: 100,
    maxSeats: 25,
    cta: 'Start Free Trial',
    ctaHref: '/signup?plan=ai',
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    price: 599.99,
    description: 'Full platform for large operations',
    features: [
      'Everything in AI-Powered',
      'Unlimited camera channels',
      'Custom AI models',
      'Revenue share management',
      '100+ report types',
      'SSO & advanced RBAC',
      'Custom integrations',
      'White-glove onboarding',
      '24/7 premium support',
    ],
    maxVehicles: 200,
    maxDevices: 500,
    maxSeats: 100,
    cta: 'Contact Sales',
    ctaHref: '/signup?plan=enterprise',
  },
];
